import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { BASE_URL } from "../../../config/config";

// Super admin: how a booking cancellation is refunded, and when an owner may
// claim their share. Tenants and owners are shown exactly this. It only ever
// applies to bookings paid AFTER it is saved — every booking keeps the terms it
// was paid under — so changing it never rewrites a promise already made.

const headers = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("ots_token") || localStorage.getItem("ots_token")}`,
});

const money = (n) => `Rs ${Math.round(n).toLocaleString()}`;
const fromApi = (bands) => bands.map((b) => ({ days: String(b.maxHours / 24), percent: String(b.percent) }));

// Same rules the server enforces, so the admin sees the problem before saving.
const problemsOf = (rows) => {
  const problems = [];
  rows.forEach((r, i) => {
    const p = Number(r.percent);
    const d = Number(r.days);
    if (r.percent === "" || !Number.isInteger(p) || p < 0 || p > 100) problems.push(`Step ${i + 1}: enter a refund between 0 and 100.`);
    if (r.days === "" || !Number.isInteger(d) || d < 1 || d > 90) problems.push(`Step ${i + 1}: enter a whole number of days from 1 to 90.`);
    else if (i > 0 && d <= Number(rows[i - 1].days)) problems.push(`Step ${i + 1}: the days must be more than the step before.`);
    if (i > 0 && p > Number(rows[i - 1].percent)) problems.push(`Step ${i + 1}: a later cancellation cannot refund more than an earlier one.`);
  });
  return problems;
};

export default function CancellationPolicyEditor() {
  const [saved, setSaved] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BASE_URL}/admin/system-settings`, { headers: headers() });
      const p = data.data.cancellationPolicy;
      setSaved(p);
      setRows(fromApi(p.bands));
    } catch (e) {
      toast.error("Could not load the cancellation policy");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const problems = useMemo(() => problemsOf(rows), [rows]);
  const changed = saved && JSON.stringify(rows) !== JSON.stringify(fromApi(saved.bands));

  const setRow = (i, key, value) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [key]: value.replace(/[^0-9]/g, "") } : r)));
  const addStep = () =>
    setRows((rs) => {
      if (rs.length >= 6) return rs;
      const last = rs[rs.length - 1];
      return [...rs, { days: String((Number(last.days) || 0) + 1), percent: last.percent }];
    });
  const removeStep = (i) => setRows((rs) => (rs.length <= 1 ? rs : rs.filter((_, j) => j !== i)));

  const save = async () => {
    const r = await Swal.fire({
      title: "Save this policy?",
      html: "This applies to bookings paid <b>from now on</b>. Bookings already paid keep the terms their tenant and owner agreed to.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, save",
    });
    if (!r.isConfirmed) return;
    setBusy(true);
    try {
      const { data } = await axios.put(
        `${BASE_URL}/admin/settings/cancellation-policy`,
        {
          bands: rows.map((row) => ({
            maxHours: Number(row.days) * 24,
            percent: Number(row.percent),
          })),
        },
        { headers: headers() }
      );
      toast.success(data.message);
      setSaved(data.data);
      setRows(fromApi(data.data.bands));
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not save the policy");
    } finally {
      setBusy(false);
    }
  };

  // Worked example on a typical booking: advance 15,000 + 3% commission.
  const advance = 15000;
  const commission = 450;

  if (loading) return <CircularProgress size={24} />;
  if (!saved) return null;

  return (
    <Card sx={{ borderRadius: 3, mt: 3, border: "1px solid #e5e7eb" }} elevation={0}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={700}>
          Cancellation & owner payout policy
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          How long a tenant can cancel an online booking for a refund, and how much they get back. When the last
          step ends the owner can claim their payment. Tenants and owners see exactly this. Currently version {saved.version}.
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          Changes apply to bookings paid <b>after</b> you save. A booking already paid keeps the policy it was paid
          under, so nobody's agreed terms change under them.
        </Alert>

        {rows.map((r, i) => {
          const pct = Number(r.percent) || 0;
          const full = pct === 100;
          const refund = full ? advance + commission : Math.floor((advance * pct) / 100);
          const owner = full ? 0 : advance - Math.floor((advance * pct) / 100);
          return (
            <Grid container spacing={1.5} alignItems="center" key={i} sx={{ mb: 1 }}>
              <Grid item xs={12} md={5}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography variant="body2" sx={{ minWidth: 56 }}>
                    {i === 0 ? "Within" : "Up to"}
                  </Typography>
                  <TextField size="small" value={r.days} onChange={(e) => setRow(i, "days", e.target.value)} sx={{ width: 80 }} inputProps={{ inputMode: "numeric" }} />
                  <Typography variant="body2">day(s) of paying</Typography>
                </Box>
              </Grid>
              <Grid item xs={7} md={3}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2">Tenant gets</Typography>
                  <TextField size="small" value={r.percent} onChange={(e) => setRow(i, "percent", e.target.value)} sx={{ width: 80 }} inputProps={{ inputMode: "numeric" }} />
                  <Typography variant="body2">%</Typography>
                </Box>
              </Grid>
              <Grid item xs={4} md={3}>
                <Typography variant="caption" color="text.secondary">
                  e.g. tenant {money(refund)} · owner {money(owner)}
                </Typography>
              </Grid>
              <Grid item xs={1}>
                {rows.length > 1 && (
                  <IconButton size="small" onClick={() => removeStep(i)} aria-label="remove step">
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          );
        })}
        <Button size="small" startIcon={<Add />} onClick={addStep} disabled={rows.length >= 6} sx={{ mb: 2 }}>
          Add a step
        </Button>

        <Box sx={{ p: 1.5, bgcolor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 2, mb: 2 }}>
          <Typography variant="body2" fontWeight={700}>
            After {rows.length ? rows[rows.length - 1].days || "…" : "…"} day(s): no refund
          </Typography>
          <Typography variant="caption" color="text.secondary">
            When the last step ends, a tenant who cancels gets nothing back, and the owner can claim their payment
            from that moment — whether or not they have withdrawn it yet.
          </Typography>
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Example: advance {money(advance)} + commission {money(commission)}. A 100% step refunds everything,
          including OTS's commission. Any lower step deducts from the <b>advance only</b>: OTS keeps its commission and
          the processing fee, and the deducted part goes to the owner. Tenants see these rules only when they pay
          online, never for cash bookings.
        </Typography>

        {problems.length > 0 && changed && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {problems[0]}
          </Alert>
        )}

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" onClick={save} disabled={!changed || problems.length > 0 || busy} sx={{ bgcolor: "#e94560", "&:hover": { bgcolor: "#d13652" } }}>
            {busy ? "Saving…" : "Save policy"}
          </Button>
          <Button
            onClick={() => setRows(fromApi(saved.bands))}
            disabled={!changed || busy}
          >
            Reset
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
